import React, { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { LearnerSettingsSectionTheme } from '../settings/learnerSettingsSectionThemes';
import { CollapsibleFadePanel } from './CollapsibleFadePanel';

/** Minimal theme for gradient pill + rule (settings modal, dashboard sections). */
export type SectionPillTheme = Pick<LearnerSettingsSectionTheme, 'pill' | 'pillRule'>;

/**
 * Horizontal rule + pill label — matches the section dividers in the learner settings modal.
 * Optional `subtitle` renders centered below the rule (e.g. next course title on the catalog).
 * Optional `description` expands below the pill via the chevron control (learner dashboard, course detail).
 */
export function SectionPillSeparator({
  label,
  subtitle,
  description,
  className = '',
  gradient = false,
  theme
}: {
  label: string;
  subtitle?: string;
  /** User-facing help; shown on demand via the chevron beside the pill. */
  description?: React.ReactNode;
  className?: string;
  /** @deprecated Prefer `theme` for per-section modal colors. */
  gradient?: boolean;
  /** Per-section themed pill + rule (quick settings modal, learner dashboard). */
  theme?: SectionPillTheme;
}) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const hasDescription =
    description != null && (typeof description !== 'string' || description.length > 0);

  const ruleClass = theme
    ? theme.pillRule
    : gradient
      ? 'h-px flex-1 self-center rounded-full bg-gradient-to-r from-transparent via-teal-500/75 to-transparent dark:via-teal-600/70'
      : 'h-px flex-1 self-center rounded-full bg-slate-300 shadow-[0_1px_0_rgba(255,255,255,0.7)] dark:bg-gray-600 dark:shadow-none';
  const pillClass = theme
    ? theme.pill
    : gradient
      ? 'max-w-[min(100%,28rem)] shrink-0 truncate rounded-full border border-teal-400/90 bg-gradient-to-r from-teal-300/90 via-teal-200/95 to-cyan-300/85 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-teal-950 shadow-md shadow-teal-900/10 dark:border-teal-700/80 dark:from-teal-900/95 dark:via-teal-950 dark:to-cyan-950/80 dark:text-teal-100'
      : 'max-w-[min(100%,28rem)] shrink-0 truncate rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 shadow-sm dark:border-gray-600 dark:bg-gray-800 dark:text-slate-400';

  const pill = <span className={pillClass}>{label}</span>;

  return (
    <div className={`flex w-full flex-col items-stretch ${className}`} role="presentation">
      <div className="flex w-full items-center gap-3" role="separator" aria-orientation="horizontal">
        <div className={ruleClass} />
        {hasDescription ? (
          <div className="flex shrink-0 items-center gap-1.5">
            {pill}
            <button
              type="button"
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
              aria-controls={detailsId}
              aria-label={expanded ? 'Hide section description' : 'Show section description'}
              title={expanded ? 'Hide description' : 'Show description'}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200/90 bg-white/95 text-slate-500 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-700 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-400 dark:hover:bg-gray-700 dark:hover:text-slate-200"
            >
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-200 ease-out ${expanded ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </button>
          </div>
        ) : (
          pill
        )}
        <div className={ruleClass} />
      </div>
      {subtitle != null && subtitle.length > 0 ? (
        <p className="mt-2 px-3 text-center text-sm font-semibold leading-snug text-slate-800 dark:text-slate-100">
          {subtitle}
        </p>
      ) : null}
      {hasDescription ? (
        <CollapsibleFadePanel expanded={expanded} panelId={detailsId}>
            <div className="mx-auto mt-1 max-w-[min(100%,32rem)] px-2 text-center">
              {typeof description === 'string' ? (
                <p className="text-[11px] font-medium leading-snug text-slate-500 dark:text-slate-400">
                  {description}
                </p>
              ) : (
                description
              )}
            </div>
        </CollapsibleFadePanel>
      ) : null}
    </div>
  );
}
